import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Camera, AlertTriangle, XCircle, Trash2, Plus, Sparkles, Save, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { guToLatin } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

export default function PhotoToBill() {
  const { t, lang, user } = useApp();
  const isOwner = user?.role === "owner";
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [thumbUrl, setThumbUrl] = useState(null);

  React.useEffect(() => { api.get("/items").then((r) => setItems(r.data)); }, []);
  // Free blob URL on unmount to prevent memory leak
  React.useEffect(() => () => { if (thumbUrl) URL.revokeObjectURL(thumbUrl); }, [thumbUrl]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReading(true); setResult(null); setRows([]);
    if (thumbUrl) URL.revokeObjectURL(thumbUrl);
    setThumbUrl(file.type?.startsWith("image/") ? URL.createObjectURL(file) : null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/photo-to-bill/read", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(r.data);
      // Enrich rows with default rate from catalog if matched
      const enriched = r.data.rows.map((row) => {
        const match = row.matched_item_id ? items.find((x) => x.id === row.matched_item_id) : null;
        return {
          ...row,
          rate: match ? match.selling_price : 0,
          unit: row.unit || (match ? match.unit : "pcs"),
        };
      });
      setRows(enriched);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("read_failed"));
    } finally { setReading(false); }
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { raw_text: "", name_en: "", name_gu: "", quantity: 1, unit: "pcs", cancelled: false, confidence: 1, needs_check: false, rate: 0 }]);
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const active = rows.filter((r) => !r.cancelled);
  const incomplete = active.filter((r) => !(r.name_en || r.name_gu) || !r.quantity || r.quantity <= 0);
  const stillFlagged = active.filter((r) => r.needs_check && !r.confirmed);

  const createBill = async () => {
    if (incomplete.length > 0) return toast.error(t("incomplete_row"));
    if (stillFlagged.length > 0) return toast.error(t("all_flagged_reviewed"));
    setBusy(true);
    try {
      const lines = active.map((r) => ({
        item_id: r.matched_item_id || null,
        name_en: r.name_en || r.name_gu,
        name_gu: r.name_gu || "",
        unit: r.unit || "pcs",
        quantity: Number(r.quantity),
        rate: Number(r.rate) || 0,
        total: Number(r.quantity) * (Number(r.rate) || 0),
      }));
      const status = "credit";
      const res = await api.post("/bills", {
        customer_name: customerName, lines, discount: 0, amount_paid: 0, status,
        notes: "From photo-to-bill", language: lang,
      });
      toast.success(t("bill_created"));
      nav(`/bills/${res.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const total = active.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.rate) || 0), 0);

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return toast.error("Name required");
    const validLines = active.filter((r) => (r.name_en || r.name_gu) && r.quantity > 0);
    if (validLines.length === 0) return toast.error("No valid lines");
    try {
      await api.post("/templates", {
        name: tplName.trim(),
        customer_name: customerName,
        lines: validLines.map((r) => ({
          item_id: r.matched_item_id || null,
          name_en: r.name_en || r.name_gu,
          name_gu: r.name_gu || "",
          unit: r.unit || "pcs",
          quantity: Number(r.quantity),
          rate: Number(r.rate) || 0,
        })),
      });
      toast.success(t("template_saved"));
      setTplOpen(false); setTplName("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5 pb-24">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("photo_bill")}</div>
        <h1 className="font-heading text-3xl font-bold text-stone-900 mt-1">{t("upload_order")}</h1>
        <p className="text-stone-500 mt-1 text-sm">{t("upload_help")}</p>
      </div>

      {!result && !reading && (
        <Card className="p-8 rounded-2xl border-dashed border-2 border-stone-300 text-center">
          <Camera className="w-12 h-12 text-[#B45309] mx-auto mb-4" />
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.xlsx,.xls,.csv,image/*,application/pdf" onChange={onFile} className="hidden" data-testid="photo-file-input" />
          <Button onClick={() => fileRef.current?.click()} data-testid="photo-upload-btn"
                  className="h-14 px-6 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold text-lg">
            <Upload className="w-5 h-5 mr-2" /> {t("upload_order")}
          </Button>
          <div className="text-xs text-stone-500 mt-3">{t("upload_help")}</div>
        </Card>
      )}

      {reading && (
        <Card className="p-10 rounded-2xl text-center" data-testid="photo-reading">
          <Sparkles className="w-8 h-8 text-[#B45309] mx-auto mb-3 animate-pulse" />
          <div className="font-heading text-lg">{t("reading")}</div>
          <div className="text-sm text-stone-500 mt-1">Gemini 3.1 Pro</div>
        </Card>
      )}

      {result && (
        <>
          {thumbUrl && (
            <Card className="p-2 rounded-2xl border-stone-200 overflow-hidden" data-testid="p2b-thumb-card">
              <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 px-2 pt-1 pb-2">{t("original_photo")}</div>
              <a href={thumbUrl} target="_blank" rel="noreferrer" className="block">
                <img src={thumbUrl} alt="Uploaded order" data-testid="p2b-thumb-img"
                     className="w-full max-h-64 object-contain rounded-xl bg-stone-100" />
              </a>
            </Card>
          )}
          <Card className="p-4 rounded-2xl border-stone-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("engine_used")}</div>
                <div className="font-mono text-sm font-semibold text-[#166534]" data-testid="engine-used">{result.engine}</div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("expected_vs_returned")}</div>
                <div className="font-mono text-sm font-semibold" data-testid="line-counts">
                  {result.returned_lines} / {result.expected_lines}
                </div>
              </div>
              {result.warnings?.length > 0 && (
                <div className="text-amber-700 text-xs flex items-center gap-1" data-testid="line-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> {result.warnings[0]}
                </div>
              )}
            </div>
          </Card>

          <Card className="p-4 rounded-2xl">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{t("customer")}</div>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="photo-customer" className="h-12 rounded-lg" placeholder={t("customer")} />
          </Card>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-heading text-xl font-semibold">{t("review_edit")}</div>
              <div className="text-xs text-stone-500 max-w-xs text-right">{t("review_help")}</div>
            </div>

            {rows.map((r, i) => {
              const flagged = r.needs_check && !r.confirmed;
              const cancelled = r.cancelled;
              return (
                <Card key={i} data-testid={`p2b-row-${i}`}
                      className={`p-4 rounded-2xl border ${cancelled ? "opacity-60 bg-stone-50" : flagged ? "bg-amber-50/60 border-l-4 border-amber-500" : "border-stone-200"}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("original_line")}</div>
                      <div className={`text-sm text-stone-700 font-gujarati mt-0.5 ${cancelled ? "line-through" : ""}`} data-testid={`p2b-raw-${i}`}>{r.raw_text || "—"}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {flagged && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-2 py-1 rounded-md flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{t("needs_check")}</span>}
                      {cancelled && <span className="text-[10px] font-bold uppercase text-stone-600 bg-stone-200 px-2 py-1 rounded-md flex items-center gap-1"><XCircle className="w-3 h-3" />{t("cancelled")}</span>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("name_en")} <span className="text-stone-400 normal-case">({t("optional")})</span></label>
                      <Input placeholder={t("name_en")} value={r.name_en} onChange={(e) => setRow(i, { name_en: e.target.value })} data-testid={`p2b-en-${i}`} className="h-11 rounded-lg" disabled={cancelled} />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("name_gu")}</label>
                      <Input placeholder={t("name_gu")} value={r.name_gu} onChange={(e) => setRow(i, { name_gu: e.target.value })} data-testid={`p2b-gu-${i}`} className="h-11 rounded-lg font-gujarati" disabled={cancelled} />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("items")} <span className="text-stone-400 normal-case">({t("optional")})</span></label>
                    <SearchableSelect
                      value={r.matched_item_id}
                      onChange={(id) => {
                        const it = id ? items.find((x) => x.id === id) : null;
                        if (it) setRow(i, { matched_item_id: it.id, name_en: it.name_en, name_gu: it.name_gu, unit: it.unit, rate: it.selling_price });
                        else setRow(i, { matched_item_id: null });
                      }}
                      options={items.map((it) => ({ id: it.id, label: it.name_en, sublabel: it.name_gu }))}
                      placeholder={`— ${t("unmatched")} —`}
                      disabled={cancelled}
                      testid={`p2b-catalog-${i}`}
                    />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("quantity")}</label>
                      <Input type="text" placeholder={t("quantity")} value={r.quantity ?? ""} onChange={(e) => setRow(i, { quantity: parseFloat(guToLatin(e.target.value)) || 0 })} data-testid={`p2b-qty-${i}`} className="h-11 rounded-lg font-mono w-full" disabled={cancelled} />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("unit")}</label>
                      <select value={r.unit || "pcs"} onChange={(e) => setRow(i, { unit: e.target.value })} data-testid={`p2b-unit-${i}`} disabled={cancelled} className="h-11 w-full rounded-lg border border-stone-300 px-2 text-sm">
                        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("rate")}</label>
                      <Input type="number" placeholder={t("rate")} value={r.rate ?? 0} onChange={(e) => setRow(i, { rate: parseFloat(e.target.value) || 0 })} data-testid={`p2b-rate-${i}`} className="h-11 rounded-lg font-mono w-full" disabled={cancelled || !isOwner && r.matched_item_id} />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("amount")}</label>
                      <div className="h-11 rounded-lg bg-stone-100 grid place-items-center font-mono text-sm font-semibold">₹{((r.quantity || 0) * (r.rate || 0)).toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button onClick={() => setRow(i, { cancelled: !r.cancelled })} data-testid={`p2b-toggle-cancel-${i}`}
                            className="h-9 px-3 rounded-lg bg-stone-100 text-xs font-medium text-stone-700">
                      {cancelled ? "Uncancel" : "Mark cancelled"}
                    </button>
                    {flagged && (
                      <button onClick={() => setRow(i, { confirmed: true, needs_check: false })} data-testid={`p2b-confirm-${i}`}
                              className="h-9 px-3 rounded-lg bg-[#166534] text-white text-xs font-medium">
                        {t("confirm")}
                      </button>
                    )}
                    <button onClick={() => delRow(i)} data-testid={`p2b-del-${i}`}
                            className="ml-auto h-9 w-9 rounded-lg bg-red-50 grid place-items-center text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              );
            })}

            <Button onClick={addRow} variant="outline" data-testid="p2b-add-row" className="w-full h-12 rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> {t("add_row")}
            </Button>
          </div>

          <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-auto md:right-6 md:bottom-6 md:w-96 bg-white border-t md:border md:rounded-2xl border-stone-200 p-3 sm:p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-30">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-stone-500">{t("total")}</div>
                <div className="font-mono text-2xl sm:text-3xl font-bold text-stone-900 truncate" data-testid="p2b-total">₹{total.toFixed(2)}</div>
                {(incomplete.length > 0 || stillFlagged.length > 0) && (
                  <div className="text-xs text-amber-700 mt-0.5" data-testid="p2b-block-reason">
                    {incomplete.length > 0 ? `${incomplete.length} incomplete` : `${stillFlagged.length} to review`}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={() => setTplOpen(true)} data-testid="p2b-save-template"
                        className="h-12 sm:h-14 w-12 sm:w-auto rounded-xl border-stone-300 font-medium" title={t("save_as_template")}>
                  <Bookmark className="w-4 h-4" />
                </Button>
                <Button onClick={createBill} disabled={busy || active.length === 0 || incomplete.length > 0 || stillFlagged.length > 0}
                        data-testid="p2b-create-bill"
                        className="h-12 sm:h-14 px-3 sm:px-5 rounded-xl bg-[#166534] hover:bg-[#14532D] disabled:bg-stone-300 text-white font-semibold text-sm sm:text-base whitespace-nowrap">
                  <Save className="w-4 h-4 mr-1 sm:mr-2" /> {t("create_bill")}
                </Button>
              </div>
            </div>
          </div>

          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{t("save_as_template")}</DialogTitle></DialogHeader>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={t("template_name")} data-testid="p2b-tpl-name" className="h-12 rounded-lg" />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setTplOpen(false)}>{t("cancel")}</Button>
                <Button onClick={saveAsTemplate} data-testid="p2b-tpl-submit" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
